/**
 * 浅色 / 深色模式。
 *
 * 用模块级状态 + `useSyncExternalStore` 而不是 Context：主题只有一个值，
 * 但页头、页脚和代码块都可能读它，挂 Provider 只会增加层级。
 *
 * 首屏防闪由 `index.html` 里的内联脚本负责，它读的是同一个 storage key，
 * 所以这里的初始值直接从 DOM 已有的 class 反推，避免第二帧跳色。
 */
import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'havorix.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set<() => void>();

function readStored(): Theme | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'dark' || raw === 'light' ? raw : null;
  } catch {
    return null;
  }
}

function readFromDom(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

let current: Theme = typeof document === 'undefined' ? 'light' : readFromDom();

function emit() {
  for (const listener of listeners) listener();
}

function apply(next: Theme, persist: boolean) {
  current = next;
  document.documentElement.classList.toggle('dark', next === 'dark');
  document.documentElement.style.colorScheme = next;
  if (persist) {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* 隐私模式下写不进去，主题在本次会话内仍然生效 */
    }
  }
  emit();
}

/** 没有显式选择时跟随系统，用户一旦手动切换就不再自动跟随。 */
function followSystem() {
  const media = window.matchMedia(DARK_QUERY);
  const onChange = (event: MediaQueryListEvent) => {
    if (readStored() === null) apply(event.matches ? 'dark' : 'light', false);
  };
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

let following = false;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!following) {
    following = true;
    followSystem();
  }
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Theme {
  return current;
}

function getServerSnapshot(): Theme {
  return 'light';
}

export function setTheme(next: Theme) {
  apply(next, true);
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const toggle = useCallback(() => {
    apply(current === 'dark' ? 'light' : 'dark', true);
  }, []);

  return { theme, setTheme, toggle };
}
